// ============================================================
// 緯度経度ユーティリティ
// ============================================================

import type { LatLon } from '../types'

const DEG_TO_RAD = Math.PI / 180
const METERS_PER_DEG_LAT = 111_320

/**
 * 短距離近似 (km スケール) で 2 点間距離 (m) を返す。
 * Haversine よりずっと安いが、東京近郊なら誤差 < 0.5%。
 */
export function distanceMeters(a: LatLon, b: LatLon): number {
  const latMid = (a.lat + b.lat) / 2
  const dy = (b.lat - a.lat) * METERS_PER_DEG_LAT
  const dx = (b.lon - a.lon) * METERS_PER_DEG_LAT * Math.cos(latMid * DEG_TO_RAD)
  return Math.hypot(dx, dy)
}

/**
 * b から見た a の方位を 0–360 度 (true north) で返す。
 */
export function bearingDeg(from: LatLon, to: LatLon): number {
  const φ1 = from.lat * DEG_TO_RAD
  const φ2 = to.lat * DEG_TO_RAD
  const Δλ = (to.lon - from.lon) * DEG_TO_RAD
  const y = Math.sin(Δλ) * Math.cos(φ2)
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ)
  const θ = Math.atan2(y, x)
  return (θ * 180 / Math.PI + 360) % 360
}

/** 方位角を方位文字 (N/NE/E/...) に変換 */
export function bearingToCompass(deg: number): string {
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']
  return dirs[Math.round(((deg % 360) / 45)) % 8]
}

/** 方位角を日本語コンパス (北/北東/東/...) に変換 */
export function bearingToCompassJa(deg: number): string {
  const dirs = ['北', '北東', '東', '南東', '南', '南西', '西', '北西']
  return dirs[Math.round(((deg % 360) / 45)) % 8]
}

/** 円形領域 (中心 + 半径m) の bounding box を 緯度経度で返す */
export function bboxAround(center: LatLon, radiusM: number): {
  minLat: number; minLon: number; maxLat: number; maxLon: number
} {
  const dLat = radiusM / METERS_PER_DEG_LAT
  const dLon = radiusM / (METERS_PER_DEG_LAT * Math.cos(center.lat * DEG_TO_RAD))
  return {
    minLat: center.lat - dLat,
    maxLat: center.lat + dLat,
    minLon: center.lon - dLon,
    maxLon: center.lon + dLon,
  }
}

/**
 * 自己中心メートル座標 → ソナー image のピクセル座標
 * @param dx, dy 自分を原点とした東(+X)・北(+Y) 方向メートル
 * @param heading 上向きに揃える方位 (度, true north)。null なら north-up
 * @param scale m → px (例: 半径500m を 48px 表示 → 0.096)
 * @param cx, cy image 内の中心ピクセル
 */
export function metersToSonarPx(
  dx: number, dy: number,
  heading: number | null,
  scale: number,
  cx: number, cy: number,
): { x: number; y: number } {
  const θ = -(heading ?? 0) * DEG_TO_RAD
  const rx = dx * Math.cos(θ) - dy * Math.sin(θ)
  const ry = dx * Math.sin(θ) + dy * Math.cos(θ)
  return {
    x: cx + rx * scale,
    y: cy - ry * scale, // SVG/Canvas は Y 軸下向きなので反転
  }
}

/** 度 → ラジアン */
export const toRad = (deg: number) => deg * DEG_TO_RAD
